/**
 * One fan on the Followers screen (ISSUE-1372): a small avatar, the name and
 * handle, a date line, and whatever actions the tab supplies. The name block
 * is a link to that fan's profile, like v1's follow tiles, so the actions
 * stay separate tap targets.
 *
 * Under 768px the actions sit on their own line below the name block. Two
 * buttons beside the name left it about 90px at 390px wide, which cut
 * "Asked Oct 30, 2026" to "Asked Oct 3..." and hid the date entirely on the
 * other tabs (QA round 1). The date line wraps instead of truncating, so it
 * is never misread. From 768px up the row is one line again.
 */
import { FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { avatarPlateStyle } from "../profile/avatarPlateStyle";
import { resolveInitials } from "../privacy/privacyScreenLogic";
import { openProfileLabel } from "./followCopy";

export type typeFollowPerson = {
  accountId: string;
  username: string;
  displayName: string;
  photoUrl?: string | null | undefined;
};

export type typeFollowPersonRowProps = {
  person: typeFollowPerson;
  caption: string;
  actions?: ReactNode | undefined;
  testId: string;
};

export const FollowPersonRow = (props: typeFollowPersonRowProps): ReactNode => {
  const { person } = props;
  const photoUrl = person.photoUrl ?? null;
  const handle = person.username.trim() === "" ? "" : `@${person.username}`;

  return (
    <li
      className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:gap-3"
      data-testid={props.testId}
    >
      <Link
        to="/fan"
        search={{ accountId: person.accountId }}
        aria-label={openProfileLabel(person.displayName)}
        className={`flex min-h-touch-target min-w-0 items-center gap-3 rounded-md md:flex-1 ${FOCUS_RING}`}
        data-testid={`${props.testId}-profile`}
      >
        {photoUrl !== null ? (
          <img
            src={photoUrl}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-caption"
            style={avatarPlateStyle(person.displayName)}
          >
            {resolveInitials({ displayName: person.displayName })}
          </span>
        )}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-body-strong text-ink">
            {person.displayName}
          </span>
          {handle === "" ? null : (
            <span className="truncate text-caption text-ink-secondary">
              {handle}
            </span>
          )}
          {props.caption === "" ? null : (
            <span
              className="text-caption text-ink-secondary"
              data-testid={`${props.testId}-caption`}
            >
              {props.caption}
            </span>
          )}
        </span>
      </Link>
      {props.actions === undefined ? null : (
        <div
          className="flex flex-wrap items-center justify-end gap-2 md:ml-auto md:shrink-0"
          data-testid={`${props.testId}-actions`}
        >
          {props.actions}
        </div>
      )}
    </li>
  );
};
